import Image from '@/components/atoms/Image';
import Wrapper from '@/components/atoms/legacy/Wrapper';
import { BusinessInformationForm } from '@/components/organisms/PartnerSignUpFlow/BusinessInformationForm';
import { ContactInformationForm } from '@/components/organisms/PartnerSignUpFlow/ContactInformationForm';
import { Success } from '@/components/organisms/PartnerSignUpFlow/Success';
import Head from 'next/head';
import { useCallback, useState } from 'react';
import { PartnerSignUpFormFields } from '../PartnerLandingTemplate/types';
import { DealershipApplicationRequest } from './types';

type SignUpFlowStep = 'contact' | 'businessInfo' | 'success';

const STEPS: SignUpFlowStep[] = ['contact', 'businessInfo', 'success'];

export const PartnerSignUpTemplate: React.FC = () => {
  const [formValues, setFormValues] = useState<PartnerSignUpFormFields>({
    firstName: '',
    lastName: '',
    phone: '',
    extension: '',
    email: '',
    comment: '',
    dealership: '',
    businessTitle: '',
    corporationName: '',
    dealerNumber: '',
    expiration: '',
    files: [],
    uploadedFile: null,
  });

  const [currentStep, setCurrentStep] = useState(0);

  const submitUserInfo = useCallback(
    async (values: Partial<DealershipApplicationRequest>) => {
      const response = await fetch('api/partner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...values,
          Makes: [],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit user info');
      }
    },
    []
  );

  const goToNextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep]);

  const goBack = useCallback(
    (values: Partial<PartnerSignUpFormFields>) => {
      setFormValues({
        ...formValues,
        ...values,
      });
      if (currentStep > 0) {
        setCurrentStep(currentStep - 1);
      }
    },
    [currentStep, formValues]
  );

  const onCompleteContactInformation = useCallback(
    (values: Partial<PartnerSignUpFormFields>) => {
      setFormValues({
        ...formValues,
        ...values,
      });
      goToNextStep();
    },
    [goToNextStep, formValues]
  );

  const onCompleteFlow = useCallback(
    async (values: Partial<PartnerSignUpFormFields>) => {
      const merged = { ...formValues, ...values };

      setFormValues(merged);

      const {
        firstName,
        lastName,
        phone: Phone,
        extension,
        email,
        comment: Comments,
        dealership: Dealership,
        businessTitle,
        corporationName,
        dealerNumber,
        expiration,
        files: Files,
      } = merged;

      await submitUserInfo({
        Name: `${firstName} ${lastName}`,
        Email: [email],
        Dealership,
        Files,
        Phone,
        Comments,
        Address: '',
        Postal: '',
        AdditionalInfo: {
          businessTitle,
          dealerNumber,
          expiration,
          corporationName,
          extension,
        },
      });
      goToNextStep();
    },
    [goToNextStep, submitUserInfo, formValues]
  );

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const atFinalScreen = currentStep === STEPS.length - 1;

  return (
    <>
      <Head>
        <title key="title">Become a Partner</title>
      </Head>
      <Wrapper>
        <div className="flex flex-col lg:flex-row">
          <div className="lg:max-width-[660px] mb-8 rounded-2xl bg-brandElectric lg:flex-1 lg:py-5">
            <div className="flex flex-row items-center lg:flex-col-reverse lg:justify-between">
              <div className="lg:self-start">
                <Image
                  priority
                  src="/images/partner-application-small.png"
                  width="180"
                  height="160"
                  alt="Becoming a partner"
                  className="rounded-l-2xl md:hidden"
                />
                <Image
                  priority
                  src="/images/partner-application-medium.png"
                  width="389"
                  height="240"
                  alt="Becoming a partner"
                  className="hidden rounded-l-2xl md:block lg:hidden"
                />
                <Image
                  priority
                  src="/images/partner-application-large.png"
                  width="615"
                  height="588"
                  alt="Becoming a partner"
                  className="hidden rounded-l-2xl lg:block"
                />
              </div>
              <div className="flex-1 text-center lg:mb-12">
                <span className="text-title20px text-white md:text-display32px lg:text-display48px">
                  Becoming <br className="lg:hidden" />
                  <span className="text-brandMidnight">a partner</span>
                </span>
              </div>
            </div>
          </div>
          <section
            className={`flex-1 lg:px-[56px] ${
              atFinalScreen ? 'flex items-center' : ''
            }`}
          >
            {currentStep === 0 && (
              <ContactInformationForm
                onSubmit={onCompleteContactInformation}
                progress={progress}
                initialValues={{
                  firstName: formValues.firstName,
                  lastName: formValues.lastName,
                  phone: formValues.phone,
                  extension: formValues.extension,
                  email: formValues.email,
                  comment: formValues.comment,
                }}
              />
            )}
            {currentStep === 1 && (
              <BusinessInformationForm
                progress={progress}
                onSubmit={onCompleteFlow}
                goBack={goBack}
                onUploadedFile={(uploadedFile: File | null) =>
                  setFormValues({ ...formValues, uploadedFile })
                }
                initialValues={{
                  dealership: formValues.dealership,
                  businessTitle: formValues.businessTitle,
                  corporationName: formValues.corporationName,
                  dealerNumber: formValues.dealerNumber,
                  expiration: formValues.expiration,
                  files: formValues.files,
                  uploadedFile: formValues.uploadedFile,
                }}
              />
            )}
            {atFinalScreen && <Success />}
          </section>
        </div>
      </Wrapper>
    </>
  );
};
